import { useEffect, useMemo, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import { useUIDSeed } from "react-uid";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { useDrawerState } from "@hmn/coolinarika-web-core/context/Drawer";

// Internal Drawer components
import { DrawerBody } from "../../../Drawer";
import styles from "./Gallery.style";
import PhotoDrawerGalleryImage from "./GalleryImage.component";

const DrawerGalleryStyled = styled.div((props) => ({ ...styles(props) }));

const fakePaginationLimit = 30;

const DrawerGallery = ({ className, ...rest }) => {
    const uid = useUIDSeed();
    const currentPageRef = useRef(1);

    const [ref, inView] = useInView({
        triggerOnce: false,
        rootMargin: "900px 0px",
        trackVisibility: true,
        delay: 200,
    });

    const { content: images = [], isActionShots } = useDrawerState();

    const [imagesToDisplay, setImagesToDisplay] = useState(images.slice(0, fakePaginationLimit));

    useEffect(() => {
        // Only faking pagination for  ActionShots, since we get all the id's from extended attributes
        if (images && inView && isActionShots) {
            const nextSlice = Math.abs(Math.floor(currentPageRef.current * fakePaginationLimit));

            if (nextSlice < images?.length) {
                setImagesToDisplay((current) => [
                    ...current,
                    ...images?.slice(nextSlice, nextSlice + fakePaginationLimit),
                ]);
            }
            currentPageRef.current += 1;
        }
    }, [images, inView, isActionShots]);

    const galleryImages = useMemo(() => {
        if (!isActionShots && !!images?.length) {
            return images;
        }

        if (isActionShots && !!imagesToDisplay?.length) {
            return imagesToDisplay;
        }

        return [];
    }, [isActionShots, images, imagesToDisplay]);

    if (!galleryImages?.length) {
        return null;
    }

    return (
        <DrawerBody>
            <DrawerGalleryStyled className={className} {...rest}>
                {galleryImages?.map((image, index) => {
                    const { id, image: imageData, slug } = image || {};
                    const imageId = id || imageData?.id || imageData;

                    return (
                        <PhotoDrawerGalleryImage
                            key={uid(imageId || index)}
                            image={image}
                            width={1280}
                            href={`/slika/${slug ? `${slug}-` : ""}${imageId}`}
                        />
                    );
                })}
            </DrawerGalleryStyled>
            <div
                style={{
                    height: 30,
                }}
                ref={ref}
            />
        </DrawerBody>
    );
};

// <PhotoDrawerGalleryImage key={uid(image?.id || index)} image={image} width={1280} />

DrawerGallery.propTypes = {
    className: PropTypes.string,
};

DrawerGallery.defaultProps = {
    className: undefined,
};

export default DrawerGallery;
